"use client";
import Link from "next/link";
import { ArrowRight, Award, Briefcase, Wrench } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { whyJoin as data } from "@/lib/data/careers";

const iconMap: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = { Award, Briefcase, Wrench };
const accentMap: Record<string, string> = {
  "Why Top Talent Chooses Us": "var(--color-crimson)",
  "The Work Is Real": "var(--color-purple)",
  "The Technology Is Proprietary": "var(--color-cyan)",
};

export function WhyJoin() {
  return (
    <Section id="why-join" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
          </div>
        </Reveal>
        <div className="mt-10 grid lg:grid-cols-[1.5fr_1fr] gap-10">
          <div className="space-y-5">
            {data.paragraphs.map((p, i) => (
              <Reveal key={i} delay={i * 60}>
                <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))]">{p}</p>
              </Reveal>
            ))}
            <Reveal delay={300}>
              <div className="pt-4 flex flex-wrap items-center gap-3">
                {data.cta.map((c, i) => (
                  <Link key={c.label} href={c.href} className={i === 0 ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm" : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"}>
                    {c.label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                ))}
              </div>
            </Reveal>
          </div>
          <Reveal delay={120}>
            <div className="glass rounded-2xl p-6 space-y-5">
              <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">The Three Pillars</div>
              {data.pillars?.map((pillar) => {
                const Icon = iconMap[Object.keys(iconMap).find((k) => pillar.title.includes(k.split(" ")[0] || "")) || ""] ?? Briefcase;
                const accent = Object.entries(accentMap).find(([k]) => pillar.title.includes(k.split(" ")[0]))?.[1] ?? "var(--color-crimson)";
                return (
                  <div key={pillar.title} className="flex items-start gap-3">
                    <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: `color-mix(in oklch, ${accent} 18%, transparent)`, boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)` }}>
                      <Icon className="h-4 w-4" style={{ color: accent }} />
                    </span>
                    <div>
                      <div className="font-semibold text-sm">{pillar.title}</div>
                      <div className="text-xs text-[rgb(var(--fg-muted))] mt-0.5 leading-relaxed">{pillar.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
